import { Line2D, Point2D, Point3D, TrussEnvelope } from '@paragon/contract-library';
import { assertExhaustiveSwitch, PartialPartial } from '@paragon/utilities';

import { SerializedEntitySnapTarget } from '../layout-snapping/EntitySnapTarget';
import { SelectableItemType } from '../selection/types';

/**
 * All the selectable item types that support geometric transformations. To add support for a new type, first ensure
 *   it can be bulk selected, then add its type here and fix all the type errors that arise.
 */
const TRANSFORMABLE_TYPES = [
  'beam',
  'ceilingPlane',
  'ceilingTray',
  'featureArea',
  'featurePatternContainer',
  'roofPlane',
  'trussEnvelope',
  'wall',
  'workLine',
] satisfies SelectableItemType[];

/**
 * The union of selectable item types that support geometric transformations.
 */
export type TransformableType = (typeof TRANSFORMABLE_TYPES)[number];

export function isTransformableType(type: SelectableItemType): type is TransformableType {
  return (TRANSFORMABLE_TYPES as string[]).includes(type);
}

export function doesTransformableTypeSupportVerticalMovement(type: TransformableType): boolean {
  switch (type) {
    case 'beam':
    case 'ceilingPlane':
    case 'ceilingTray':
    case 'featurePatternContainer':
    case 'roofPlane':
    case 'trussEnvelope':
    case 'wall':
      return true;

    case 'featureArea':
    case 'workLine':
      return false;

    default:
      assertExhaustiveSwitch(type);
  }
}

export function doesTransformableTypeSupportLevelReassignment(type: TransformableType): boolean {
  switch (type) {
    case 'beam':
    case 'ceilingPlane':
    case 'ceilingTray':
    case 'featureArea':
    case 'featurePatternContainer':
    case 'roofPlane':
    case 'trussEnvelope':
    case 'wall':
      return true;

    case 'workLine':
      return false;

    default:
      assertExhaustiveSwitch(type);
  }
}

interface TransformationBase {
  readonly duplicate: boolean;
  readonly reassign: { levelGuid: string; featurePatternContainerGuid?: string | null } | false;
  readonly preserveLinkedComponents?: boolean;
}

/**
 * A blueprint for a movement transformation which could be applied to a selection of transformable items.
 */
export interface MovementTransformation extends TransformationBase {
  readonly type: 'movement';
  readonly origin: Point3D;
  readonly destination: Point3D;
}

/**
 * A blueprint for a rotation transformation which could be applied to a selection of transformable items.
 */
export interface RotationTransformation extends TransformationBase {
  readonly type: 'rotation';
  readonly center: Point2D;
  readonly angle: number;
}

/**
 * A blueprint for a mirror transformation which could be applied to a selection of transformable items.
 */
export interface MirrorTransformation extends TransformationBase {
  readonly type: 'mirror';
  readonly center: SerializedEntitySnapTarget;
}

/**
 * A blueprint for some transformation which could be applied to a selection of transformable items. Use
 *   `prepareTransformation` to obtain a instance that can actually be applied to points, lines, and angles to transform
 *   them.
 */
export type ITransformation = MovementTransformation | RotationTransformation | MirrorTransformation;

export type TransformationType = ITransformation['type'];

export function transformationTypeToPresentParticiple(type: TransformationType): string {
  switch (type) {
    case 'movement':
      return 'moving';

    case 'rotation':
      return 'rotating';

    case 'mirror':
      return 'mirroring';

    default:
      throw new Error(`Unknown transformation type: ${type}`);
  }
}

// when updating these types, ensure you also update `isTransformationFullyDefined`
export type PartialMovementTransformation = PartialPartial<MovementTransformation, 'origin' | 'destination'>;
export type PartialRotationTransformation = PartialPartial<RotationTransformation, 'center'>;
export type PartialMirrorTransformation = PartialPartial<MirrorTransformation, 'center'>;

export type IPartialTransformation =
  PartialMovementTransformation | PartialRotationTransformation | PartialMirrorTransformation;

/**
 * An `ITransformer` can be applied to points, lines, and angles to transform them.
 *
 * Create one using `prepareTransformation` and apply it to objects using the functions in `application.ts` or using the
 *   `applyTransformerToSelectedObjects` action.
 */
export interface ITransformer {
  applyToLevelGuid(guid: string | null): string | null;
  applyToFeaturePatternContainerGuid(envelope: TrussEnvelope): string | null;

  applyToPoint(point: Point2D): Point2D;
  applyToLine(line: Line2D): Line2D;

  applyToElevation(elevation: number): number;

  applyToRotation(angle: number): number;

  applyToJustification<T>(justification: T | 'Back' | 'Front'): T | 'Back' | 'Front';

  applyToHorizontalAnchor(anchor: 'Left' | 'Right' | 'Center'): 'Left' | 'Right' | 'Center';
  // applyToVerticalAnchor does not exist. See the note on `prepareMirrorTransformation#applyToRotation`.
}
